import React, { useState } from 'react';
import { base44 } from '@/api/base44Client';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2, X, AlertTriangle, CheckCircle2, GitMerge } from 'lucide-react';

export default function MergeAccountsModal({ customers, onClose, prefill }) {
  const qc = useQueryClient();
  const [primary, setPrimary] = useState(prefill?.primary || '');
  const [secondary, setSecondary] = useState(prefill?.secondary || '');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  // Build a unique email list from registered users + allow free text (legacy emails may not be registered)
  const emailOptions = Array.from(new Set(
    customers.map((c) => c.email).filter(Boolean)
  )).sort();

  const runMerge = async () => {
    if (!primary || !secondary) return;
    setBusy(true);
    setError('');
    setResult(null);
    try {
      const res = await base44.functions.invoke('mergeCustomerAccounts', {
        primary_email: primary,
        secondary_email: secondary,
      });
      setResult(res.data);
      qc.invalidateQueries({ queryKey: ['admin-users'] });
      qc.invalidateQueries({ queryKey: ['admin-orders'] });
    } catch (e) {
      setError(e?.response?.data?.error || e.message || 'Merge failed');
    } finally {
      setBusy(false);
    }
  };

  const inputCls = 'w-full bg-[#0d0d0f] border border-white/10 text-white h-11 px-3 text-sm focus:border-primary/60 outline-none';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80" onClick={onClose}>
      <div className="w-full max-w-lg bg-[#111115] border border-white/10 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-5">
          <div>
            <p className="text-primary text-[10px] font-bold tracking-widest mb-1">CUSTOMER ADMIN</p>
            <h3 className="text-white font-black text-sm uppercase">Merge Accounts</h3>
          </div>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X className="w-5 h-5" /></button>
        </div>

        <p className="text-white/40 text-xs leading-relaxed mb-4">
          Consolidate a customer's history under one email. All orders, bookings, gift cards, reviews, returns and
          session results linked to the <span className="text-white">secondary</span> email are reassigned to the
          <span className="text-white"> primary</span> email. Useful when the same person has orders under an eBay alias
          and their real email.
        </p>

        <div className="space-y-4 mb-4">
          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">PRIMARY EMAIL (KEEP)</label>
            <input
              list="merge-emails"
              value={primary}
              onChange={(e) => setPrimary(e.target.value)}
              placeholder="e.g. customer@gmail.com"
              className={inputCls}
            />
          </div>
          <div>
            <label className="text-white/40 text-[10px] font-bold tracking-widest mb-2 block">SECONDARY EMAIL (MERGE IN)</label>
            <input
              list="merge-emails"
              value={secondary}
              onChange={(e) => setSecondary(e.target.value)}
              placeholder="e.g. 20a537…@members.ebay.com"
              className={inputCls}
            />
          </div>
          <datalist id="merge-emails">
            {emailOptions.map((em) => <option key={em} value={em} />)}
          </datalist>
        </div>

        <div className="flex items-start gap-2 border border-yellow-500/20 bg-yellow-500/5 p-3 mb-4">
          <AlertTriangle className="w-4 h-4 text-yellow-400 shrink-0 mt-0.5" />
          <p className="text-yellow-200/70 text-xs">This reassigns data only. Base44 login accounts themselves cannot be deleted or merged here.</p>
        </div>

        {error && (
          <div className="flex items-start gap-2 border border-red-500/30 bg-red-500/5 p-3 mb-4">
            <AlertTriangle className="w-4 h-4 text-red-400 shrink-0 mt-0.5" />
            <p className="text-red-300 text-xs">{error}</p>
          </div>
        )}

        {result && (
          <div className="border border-primary/30 bg-primary/5 p-4 mb-4">
            <p className="flex items-center gap-2 text-white font-bold text-sm mb-2"><CheckCircle2 className="w-4 h-4 text-primary" /> Merge complete</p>
            <div className="space-y-1">
              {Object.entries(result.results || {}).map(([entity, count]) => (
                <p key={entity} className="text-white/50 text-xs flex justify-between">
                  <span>{entity}</span>
                  <span className="text-white font-bold">{typeof count === 'number' ? `${count} updated` : JSON.stringify(count)}</span>
                </p>
              ))}
            </div>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 pt-2 border-t border-white/5">
          <button onClick={onClose} className="text-white/50 hover:text-white text-xs font-bold tracking-wider px-4 py-2.5">
            {result ? 'CLOSE' : 'CANCEL'}
          </button>
          <button
            onClick={runMerge}
            disabled={!primary || !secondary || primary === secondary || busy}
            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-xs tracking-wider px-5 py-2.5 transition-all disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <GitMerge className="w-4 h-4" />}
            {busy ? 'MERGING…' : 'MERGE ACCOUNTS'}
          </button>
        </div>
      </div>
    </div>
  );
}